// Which screen shows. One pure function, nextNav(), is the only place these fields change: main.ts's
// go() applies its answer to `ui`, then draws and scrolls. Refreshes (pull, background, foreground,
// after a save) only redraw: they never call go(), so they never change or reopen a screen.
//
// Screens stack like this: a tab (or Settings), a category screen over a tab, a Settings page over
// the menu, the Splits screen over whatever was showing. A tab-bar tap always lands on that tab's
// top level (nothing over it, Activity unfiltered); Back pops one level.

import type { ScrollMemory } from './scroll';
import type { Tab } from './state';
import type { SettingsPage } from './views/settings';

export interface NavState {
  tab: Tab;
  /** The top-level tab Settings was opened from (its Back returns there). */
  from: Exclude<Tab, 'settings'>;
  /** A category screen over the tab. */
  categoryId: string | null;
  /** A Settings page over the menu (null: the menu). */
  settingsPage: SettingsPage | null;
  /** "What Hush has learned" (a Settings page with a search box). */
  learned: { search: string } | null;
  /** The Splits screen over whatever was showing. */
  settle: boolean;
  /** Activity's list: 'all', 'new', 'review' or a category id. */
  filter: string;
  /**
   * The month being viewed (YYYY-MM), or null for the household's active month. Every tab shows
   * it; a tab-bar tap keeps it; "Back to November" and starting a month set it back to null.
   */
  month: string | null;
}

export type NavTarget =
  /** A tab-bar tap: that tab's top level, from anywhere. */
  | { to: 'tab'; tab: Exclude<Tab, 'settings'> }
  /** A category screen over the current tab. */
  | { to: 'category'; id: string }
  /** Activity with a list filter ("See activity", the chips, "See all"). */
  | { to: 'activity'; filter: string }
  /** The gear (the menu), or one Settings page. */
  | { to: 'settings'; page?: SettingsPage | null }
  /** The Splits screen. */
  | { to: 'splits' }
  /** Another month (the month list, the arrows), or null: back to the active month. Lands on Overview. */
  | { to: 'month'; month: string | null }
  | { to: 'back' }
  /** Signed out, a new account or sample data: Overview, nothing open. */
  | { to: 'start' };

export const NAV_START: NavState = { tab: 'overview', from: 'overview', categoryId: null, settingsPage: null, learned: null, settle: false, filter: 'all', month: null };

/** The nav fields of a larger object (ui). */
export const navOf = (s: NavState): NavState => ({
  tab: s.tab,
  from: s.from,
  categoryId: s.categoryId,
  settingsPage: s.settingsPage,
  learned: s.learned,
  settle: s.settle,
  filter: s.filter,
  month: s.month,
});

/**
 * The screen after `t`, and whether it opens at the top (`fresh`) rather than where it was left.
 * `enteredSettings`: Settings was opened from a tab, so its menu starts at the top too.
 */
export function nextNav(s: NavState, t: NavTarget): { state: NavState; fresh: boolean; enteredSettings: boolean } {
  const top = { categoryId: null, settingsPage: null, learned: null, settle: false };
  switch (t.to) {
    case 'start':
      return { state: { ...NAV_START }, fresh: true, enteredSettings: false };
    case 'tab': {
      // Activity's top level is the whole list; a filtered one was a screen of its own.
      const filter = t.tab === 'activity' ? 'all' : s.filter;
      const fresh = t.tab === 'activity' && s.filter !== 'all';
      return { state: { ...s, ...top, tab: t.tab, filter }, fresh, enteredSettings: false };
    }
    case 'category':
      return { state: { ...s, categoryId: t.id, settle: false }, fresh: true, enteredSettings: false };
    case 'activity':
      return { state: { ...s, ...top, tab: 'activity', filter: t.filter }, fresh: true, enteredSettings: false };
    case 'settings': {
      const entering = s.tab !== 'settings';
      const page = t.page ?? null;
      return {
        state: {
          ...s,
          ...top,
          tab: 'settings',
          from: entering ? (s.tab as Exclude<Tab, 'settings'>) : s.from,
          settingsPage: page,
          learned: page === 'learned' ? { search: '' } : null,
        },
        fresh: true,
        enteredSettings: entering,
      };
    }
    case 'splits':
      return { state: { ...s, settle: true }, fresh: true, enteredSettings: false };
    case 'month':
      return { state: { ...s, ...top, tab: 'overview', month: t.month }, fresh: true, enteredSettings: false };
    case 'back': {
      if (s.settle) return { state: { ...s, settle: false }, fresh: false, enteredSettings: false };
      if (s.learned || s.settingsPage) return { state: { ...s, learned: null, settingsPage: null }, fresh: false, enteredSettings: false };
      if (s.categoryId) return { state: { ...s, categoryId: null }, fresh: false, enteredSettings: false };
      if (s.tab === 'settings') return { state: { ...s, tab: s.from }, fresh: false, enteredSettings: false };
      return { state: s, fresh: false, enteredSettings: false };
    }
  }
}

/**
 * One screen change with its scroll (main.ts's go() is this plus drawing): remember where the
 * current screen was, switch, and say where the new one scrolls to (where it was left, or the
 * top when it opens fresh). Settings opened from a tab starts its menu at the top.
 */
export function navigateTo(scrolls: ScrollMemory, s: NavState, t: NavTarget, y: number): { state: NavState; y: number } {
  scrolls.save(s, y);
  const next = nextNav(s, t);
  if (next.enteredSettings) scrolls.forget({ tab: 'settings', categoryId: null });
  return { state: next.state, y: scrolls.restore(next.state, next.fresh) };
}
